/**
 * The narrow shapes the session logic drives, instead of `RTCPeerConnection`
 * and `WebSocket` themselves.
 *
 * This is not abstraction for its own sake — it is the only way the handshake
 * gets a real test. The browser types hand their callbacks a `MessageEvent` /
 * `RTCPeerConnectionIceEvent`, and a fake that satisfies those is most of a
 * DOM re-implementation; under `strictFunctionTypes` a hand-written stub is not
 * even assignable to them. So the session speaks these five-method interfaces,
 * `browser-rtc.ts` adapts the real objects onto them in about forty lines, and
 * a test wires two fakes to each other and watches an offer become an open
 * channel.
 */

import type { IceConfig } from "./ice.ts";

export interface SessionDescription {
  readonly type: "offer" | "answer";
  readonly sdp: string;
}

export interface IceCandidateInit {
  readonly candidate: string;
  readonly sdpMid: string | null;
  readonly sdpMLineIndex: number | null;
}

export interface DataChannelLike {
  send(frame: string): void;
  close(): void;
  onopen: (() => void) | null;
  onmessage: ((frame: string) => void) | null;
  onclose: (() => void) | null;
}

export interface PeerConnectionLike {
  createDataChannel(label: string): DataChannelLike;
  createOffer(): Promise<SessionDescription>;
  createAnswer(): Promise<SessionDescription>;
  setLocalDescription(description: SessionDescription): Promise<void>;
  setRemoteDescription(description: SessionDescription): Promise<void>;
  addIceCandidate(candidate: IceCandidateInit): Promise<void>;
  close(): void;
  /** `null` marks the end of gathering. */
  onicecandidate: ((candidate: IceCandidateInit | null) => void) | null;
  /** Fires on the answering side, when the offerer's channel arrives. */
  ondatachannel: ((channel: DataChannelLike) => void) | null;
  onstatechange: ((state: string) => void) | null;
}

export type PeerFactory = (ice: IceConfig) => PeerConnectionLike;

/** The relay socket, same treatment as the peer connection above. */
export interface SocketLike {
  send(frame: string): void;
  close(): void;
  onopen: (() => void) | null;
  onmessage: ((frame: string) => void) | null;
  onclose: (() => void) | null;
  onerror: ((message: string) => void) | null;
}

export type SocketFactory = (url: string) => SocketLike;

/** Connection states that mean the peer link is gone for good. */
export const DEAD_PEER_STATES: readonly string[] = ["failed", "closed", "disconnected"];

/**
 * The label the offering peer opens its channel with. Reliable and ordered
 * (the default): the ledger tolerates loss and reordering and the protocol
 * carries redundant filings anyway, but a stalled tick costs a visible hitch,
 * so there is nothing to gain from an unreliable channel carrying 40 bytes a
 * tick.
 */
export const DUEL_CHANNEL_LABEL = "red-opus-duel";
